import React, { useState } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const InvestorEditModal = ({ investor, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(investor.name);

  const updateInvestor = async () => {
    try {
      await api.put(`/investors/${investor.id}`, { name });
      toast.success(t("investors.updated"));
      onUpdated();
      onClose();
    } catch {
      toast.error(t("investors.updateFailed"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-base-100 rounded-2xl border border-base-200 shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-base-200">
          <h2 className="text-lg font-bold text-base-content">{t("investors.edit")}</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-square">✕</button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
              {t("investors.name", { defaultValue: "Nom" })}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && updateInvestor()}
              className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-base-200">
          <button onClick={onClose} className="btn btn-ghost">
            {t("buttons.cancel")}
          </button>
          <button onClick={updateInvestor} className="btn btn-primary">
            {t("buttons.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const TransactionEditModal = ({ transaction, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    type: transaction.type,
    amount: transaction.amount,
    date: transaction.date,
    category: transaction.category || "",
  });

  const updateTransaction = async () => {
    try {
      await api.put(`/transactions/${transaction.id}`, {
        investor_id: transaction.investor_id,
        ...form,
      });
      toast.success(t("transactions.updated"));
      onUpdated();
      onClose();
    } catch {
      toast.error(t("transactions.updateFailed"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-base-100 rounded-2xl border border-base-200 shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-base-200">
          <h2 className="text-lg font-bold text-base-content">{t("transactions.edit")}</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-square">✕</button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
              {t("transactions.type")}
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
            >
              <option value="deposit">{t("transactions.deposit")}</option>
              <option value="withdrawal">{t("transactions.withdrawal")}</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
              {t("transactions.amount")}
            </label>
            <input
              type="number"
              placeholder={t("transactions.amount")}
              className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) })}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
              {t("transactions.date")}
            </label>
            <input
              type="date"
              className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">
              {t("transactions.categoryOptional")}
            </label>
            <input
              type="text"
              placeholder={t("transactions.categoryOptional")}
              className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none w-full"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-base-200">
          <button onClick={onClose} className="btn btn-ghost">
            {t("buttons.cancel")}
          </button>
          <button onClick={updateTransaction} className="btn btn-primary">
            {t("buttons.save")}
          </button>
        </div>
      </div>
    </div>
  );
};
